import React, { useState } from "react";
import { Button, Flex, ProviderConsumer, Menu, tabListBehavior } from '@fluentui/react-northstar';
import { KBList } from "./KBList";
import { kbDataService } from '../shared/services/kb.service';
import { Communication } from '@fluentui/react-teams';
import { Providers } from "@microsoft/mgt-element/dist/es6/providers/Providers";
import { Prompt } from 'react-router';
import ConfigCSS from '../config/config.module.css';
import { Toolbar } from "../shared/components/Toolbar";
import { appState, KBData, SynonymData, useSetState } from "../AppState";
import { platformService } from "../shared/services/platform.service";
import { AppKBStateContext, useSetKBState } from "../AppKBState";
import { CheckLogin } from "../shared/components/CheckLogin";
import { Helmet } from 'react-helmet';
import { LoadingScreen } from "../shared/components/LoadingScreen";
import { pick, groupBy } from "lodash";
import { KBToolbar } from "./KBToolbar";
import { SynonymTable } from "./SynonymList";
import { CONTACT_ADMIN, REFRESH_SESSION, SOMETHING_WENT_WRONG, UNAUTHORIZED_ACCESS, LICENSE_FEATURE, FILTERS_STRINGS, KNOWLEDGEAGENT, ADMINISTRATORS, ANALYSTS } from "../shared/utils/constants";
import { useTranslation } from "react-i18next";
import { getCachedFeature } from "../shared/cache/FeatureCache";
import { UpgradeNeeded } from '../shared/components/UpgradeNeeded';
import { KbPlayground } from "./KbPlayground";
import UpgradeSVG from './../../svg/upgrade.svg';
import { SortWithDefaultTemplateFirst } from "../templates/TicketTemplatesTable";
import { ThemeColorScheme } from "../shared/common/TeamsTheme";

export const KB_Template_Filter = `${FILTERS_STRINGS.FILTERS_URL_PARAMS} and NoCustomForm eq false and CardResponseJson ne '' and CardResponseJson ne null`;

export function KBMain() {
  const { t } = useTranslation();
  const api = new platformService();

  const [data, setData] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [menu, setMenu] = useState(0);

  const [isError, setIsError] = useState(false);
  const [errCode, setErrCode] = useState(0);
  const [templates, setTemplates] = useState([]);

  let setState = useSetKBState();
  const currentState = appState();
  const setContextState = useSetState();

  const fetchData = async () => {

if (currentState.supportgroups.length<=0)
    {
      const [sGroup, teamList] = await Promise.all([
              api.getSupportGroups(`?$expand=Members(select=Id,AadObjectId,FullName,UserName,Email)`),
              api.getTeamsSafe(FILTERS_STRINGS.TICKET_TEAMS),
            ]);

      setContextState(preState => ({
        ...preState,
        supportgroups: sGroup.data.value,
        teams: teamList
      }));
    }

    setState((state) => {
      return {
        ...state,
        loadingKB: {
          isLoading: true,
          message: t('knowledge.loading-knowledge')
        }
      }
    })
    let values = [];
    let synonyms = [];

    const canEditKnowledge = currentState.userRoles.roles.some(r => [ADMINISTRATORS, ANALYSTS, KNOWLEDGEAGENT].includes(r));

    try {
      const templateDeflection = await getCachedFeature(LICENSE_FEATURE.TemplateDeflection);

      const result = await Promise.all([
        kbDataService.fetchAPI(),
        templateDeflection ? api.getTicketTemplates(KB_Template_Filter) : Promise.resolve({ data: { value: [] } }),
        canEditKnowledge ? kbDataService.fetchSynonyms() : Promise.resolve({ data: { value: [] } }),
        getCachedFeature(LICENSE_FEATURE.TicketDeflection),
        getCachedFeature(LICENSE_FEATURE.ServiceCatalog)
      ]);

      values = JSON.parse(result[0].data.value);
      values = values.sort((a, b) => b["id"] - a["id"]);

      let ids = Array.from(new Set(values.flatMap(obj => [obj.createdById, obj.modifiedById]).filter(id => id > 0))).join(',');
      let params = ids ? `?$filter=Id in (${ids})` : '';
      let users = [];
      if (params) {
        users = (await api.getUsers(params)).data.value;
      }

      const groups = groupBy(values, ({ source }) => source);
      const offers = values.filter(x => x.enableOffering);

      const activeIndex = parseInt(localStorage.getItem("sc_active_tab") ?? "0", 10);
      const items = [
        {
          key: 'qnapairs',
          content: <div>{t('knowledge.qnapairs')} <span className='list_filter_btn_badge' style={{ backgroundColor: 'var(--brand-border-active)' }}>{values.length}</span></div>,
        },
        {
          key: 'kbpairs',
          content: <div>{t('knowledge.kbpairs')} <span className='list_filter_btn_badge' style={{ backgroundColor: 'var(--brand-border-active)' }}>{groups['Editorial']?.length ?? 0}</span></div>,
        },
        {
          key: 'templatepairs',
          content: <div>{t('knowledge.templatepairs')} <span className='list_filter_btn_badge' style={{ backgroundColor: 'var(--brand-border-active)' }}>{groups['Template']?.length ?? 0}</span> {!templateDeflection && <UpgradeSVG height={20} width={20} className='inline-block' />}</div>,
          disabled: !templateDeflection
        },
        {
          key: 'ropairs',
          content: <div>{t('knowledge.ropairs')} <span className='list_filter_btn_badge' style={{ backgroundColor: 'var(--brand-border-active)' }}>{offers.length}</span> {!result[4] && <UpgradeSVG height={20} width={20} className='inline-block' />}</div>,
          disabled: !result[4]
        }
      ];

      if (canEditKnowledge) {
        items.push(
          {
            key: 'synonyms',
            content: <div>{t('knowledge.synonyms')}</div>
          },
          {
            key: 'chat',
            content: <div>{t('knowledge.chat')}</div>
          }
        );
      }

      synonyms = result[2].data.value;

      setState(state => {
        return {
          ...state,
          knowledgeBase: values,
          synonyms: synonyms,
          tab: activeIndex,
          users: users,
          menuItems: items
        }
      })
      kbDataService.initData(values, synonyms);
      setIsError(false);
      let templateResults = SortWithDefaultTemplateFirst(result[1].data.value);
      setTemplates(templateResults);
    } catch (e: any) {
      setIsError(true);
      setErrCode(e.response.status);
    }

    setState((state) => {
      return {
        ...state,
        loadingKB: {
          ...state.loadingKB,
          isLoading: false
        }
      }
    })
    setIsLoading(false);
    setData((prevState) => {
      return Object.assign([], values);
    });
  };

  const loadPage = () => {
    fetchData();
  }

  const processKB = (knowledgeBase: KBData[]) => {
    let processedKB = {
      title: "KB",
      find: true,
      emptySelectionActionGroups: {
        g1: {
          a1: { title: t('knowledge.add-qn-a-pair'), icon: "Add", subject: "add kb" },
        },
      },
      columns: {
        questions: {
          title: t('knowledge.questions')
        },
        answer: {
          title: t('knowledge.answer')
        },
        options: {
          title: "",
          minWidth: 20
        }
      },
      rows: {},
      templates: templates
    };
    if (knowledgeBase) {
      knowledgeBase.map((item: KBData) => {
        processedKB.rows["row-" + item.id] = {
          questions: item.questions.join("|"),
          answer: item.answer,
          options: ["delete"],
          id: item.id,
          source: item.source,
          createdById: item.createdById,
          modifiedById: item.modifiedById,
          status: item.status,
          agentOnly: item.agentOnly,
          lastUpdatedDateTime: item.lastUpdatedDateTime
        }
      })
    }
    return processedKB;
  }
  const processSynonyms = (synonyms: SynonymData[]) => {
    let processed = {
      title: "KB",
      find: true,
      emptySelectionActionGroups: {
        g1: {
          a1: { title: t('knowledge.add-synonym-pair'), icon: "Add", subject: "add synonym" },
        },
      },
      columns: {
        synonyms: {
          title: "Synonyms"
        },
        options: {
          title: "",
          minWidth: 20
        }
      },
      rows: {}
    };
    if (synonyms) {
      synonyms.forEach((item: SynonymData, idx: number) => {
        processed.rows["row-" + idx] = {
          alterations: item.alterations,
          options: ["delete"],
          id: idx
        }
      });
    }
    return processed;
  }

  const errConfig = {
    fields: {
      title: (errCode == 401 || errCode == 403) ? UNAUTHORIZED_ACCESS : SOMETHING_WENT_WRONG,
      desc: CONTACT_ADMIN
    }
  }

  const KbBase = (props) => {
    const [isTicketDeflectionEnabled, setisTicketDeflectionEnabled] = useState(true);
    (async () => setisTicketDeflectionEnabled((await getCachedFeature(LICENSE_FEATURE.TicketDeflection))))();

    return (

      (isError) ? (<ProviderConsumer render={(globalTheme) => (
        <>
          <Toolbar globalTheme={globalTheme} title="Settings > Knowledge" hideSave={true} />
          <Flex className={ConfigCSS.configBody}>

            <Flex.Item grow>
              <>
                <Communication {...errConfig} />
                {errCode === 401 && (<Button content={REFRESH_SESSION} primary onClick={e => { Providers.globalProvider.login(); }} />)}
              </>
            </Flex.Item>
          </Flex>
        </>
      )} />)
        : <AppKBStateContext.Consumer>
          {(state) => {
            const toolbarProps = pick(props, [
              'filtersSingleSelect',
              'find',
              'onInteraction'
            ]);
            return (
              <ProviderConsumer render={(globalTheme) => (
                <>
                  {!isTicketDeflectionEnabled && <UpgradeNeeded
                    toolbarText={t('tab-title.knowledge')}
                    headerText={t('knowledge.ticket-deflection.no-access')}
                    subheaderText={t('knowledge.ticket-deflection.upgrade-plan')} />}
                  <KBToolbar
                    globalTheme={globalTheme}
                    state={state}
                    {...toolbarProps}
                    actionGroups={{}}
                    onInteraction={() => { }}
                    aria-controls="fluentui-teams__list-content"
                    aria-label="List content controls"
                  />
                  <Menu defaultActiveIndex={state.tab} items={state.menuItems} underlined primary accessibility={tabListBehavior} aria-label="Knowledge "
                    onActiveIndexChange={(e, p: any) => {
                      setState(state => ({ ...state, tab: p.activeIndex }));
                      localStorage.setItem('sc_active_tab', p.activeIndex);
                    }} styles={{ ...ThemeColorScheme(globalTheme.siteVariables) }} />
                  {(state.tab === 0) && (<KBList {...processKB(state.knowledgeBase)} ifEmpty={{ header: t('knowledge.empty.empty-knowledge.header'), subHeader: t('knowledge.empty.empty-knowledge.subHeader') }} source="All" />)}
                  {(state.tab === 1) && (<KBList {...processKB(state.knowledgeBase)} ifEmpty={{ header: t('knowledge.empty.empty-knowledge.header'), subHeader: t('knowledge.empty.empty-knowledge.subHeader') }} source="Editorial" />)}
                  {(state.tab === 2) && (<KBList {...processKB(state.knowledgeBase)} ifEmpty={{ header: t('knowledge.empty.empty-knowledge.header'), subHeader: t('knowledge.empty.empty-knowledge.subHeader') }} source="Template" />)}
                  {(state.tab === 3) && (<KBList {...processKB(state.knowledgeBase)} ifEmpty={{ header: t('knowledge.empty.empty-knowledge.header'), subHeader: t('knowledge.empty.empty-knowledge.subHeader') }} source="RequestOffering" />)}
                  {(state.tab === 4) && (<SynonymTable {...processSynonyms(state.synonyms)} ifEmpty={{ header: t('knowledge.empty.empty-synonyms.header'), subHeader: t('knowledge.empty.empty-knowledge.subHeader') }} />)}
                  {(state.tab === 5) && (<KbPlayground />)}
                </>
              )} />
            )
          }}
        </AppKBStateContext.Consumer>

    )
  }
  return (
    <CheckLogin onSignedIn={loadPage}>
      <AppKBStateContext.Consumer>
        {(state) => {
          return (
            <>
              <Helmet>
                <title>{t('tab-title.knowledge')} - {t('tab-title.postfix')}</title>
              </Helmet>
              <Prompt
                when={state.editedKB.length > 0}
                message={t('knowledge.massage')}
              />
              {(state.loadingKB.isLoading) ? <LoadingScreen message={state.loadingKB.message} /> : <KbBase />}
            </>
          )
        }}
      </AppKBStateContext.Consumer>
    </CheckLogin>
  )
}